import { Avatar } from "@/components/ui/avatar";
import { AvatarFallback } from "@/components/ui/avatar";
import { FiUser } from "react-icons/fi";
import { AssigneeSelect } from "@/components/AssigneeSelect";
import axios from '@/utils/axios';
import { useNavigate } from 'react-router-dom';
import { PrioritySelect } from "./PrioritySelect";
import { ReleaseIconSelect } from "./ReleaseIconSelect";
import { Paperclip } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { TaskContextMenu } from './TaskContextMenu';

export function TaskCard({ task, organizationSlug, className, onTaskUpdate, ...props }) {
  const navigate = useNavigate();
  const hasAttachments = task.attachments && task.attachments.length > 0;
  const visibleLabelsCount = task.labels?.length || 0;

  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1800);
  const debouncedWidth = useDebounce(width, 500);

  useEffect(() => {
    const updateWidth = () => setWidth(window.innerWidth);
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const maxVisibleLabels = hasAttachments || debouncedWidth < 1800 ? 1 : 2;
  const hiddenLabelsCount = Math.max(0, visibleLabelsCount - maxVisibleLabels);

  const handleTaskChange = (updatedTask) => {
    // Map the updated task to match the project's task structure
    const mappedTask = {
      ...task, // Start with current task to preserve existing structure
      ...updatedTask, // Overlay with updated data
      id: updatedTask._id || task.id,
      status: updatedTask.status?.id || updatedTask.status || task.status,
      assignee: updatedTask.assignee,
      priority: updatedTask.priority || task.priority,
      order: task.order // Preserve the order from the original task
    };

    if (onTaskUpdate) {
      onTaskUpdate(mappedTask);
    }
  };

  const handleCardClick = (e) => {
    // Prevent click if clicking on the priority or assignee selectors
    if (e.target.closest('.task-action')) {
      e.stopPropagation();
      return;
    }
    navigate(`/${organizationSlug}/plan/${task.key}`);
  };

  return (
    <TaskContextMenu task={task} organizationSlug={organizationSlug}>
      <div
        onClick={handleCardClick}
        className={`max-w-full bg-card hover:bg-muted/90 border border-border/50
          hover:border-border/80 rounded-lg p-3
          transition-all duration-200 cursor-pointer space-y-2.5
          shadow-sm hover:shadow-md
          dark:shadow-none dark:bg-muted/80 dark:hover:bg-muted/90 dark:border-border/50
          dark:hover:border-border/80 ${className}`}
        {...props}
      >
        <h3 className="text-sm font-medium break-words">{task.summary}</h3>

        {/* Task Details Row */}
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[auto_1fr] gap-2 items-start">
            <span className="text-xs font-medium text-muted-foreground/80 whitespace-nowrap flex items-center gap-1">
              {task.key}
              {hasAttachments && (
                <Paperclip className="w-2.5 h-2.5 md:w-3 md:h-3 text-muted-foreground/80 ml-1" />
              )}
            </span>
            <div className="flex flex-wrap items-center gap-1 justify-end">
              <div className="flex flex-wrap items-center gap-1.5">
                {task.labels && task.labels.slice(0, maxVisibleLabels).map((label) => (
                  <div
                    key={label._id}
                    className="text-[10px] px-1.5 h-5 border border-neutral-700 rounded-sm inline-flex items-center text-muted-foreground gap-1.5 min-w-0 max-w-[100px]"
                  >
                    <div className={`w-2 h-2 rounded-full ${label.color} shrink-0`} />
                    <span className="truncate">{label.name}</span>
                  </div>
                ))}
                {hiddenLabelsCount > 0 && (
                  <div className="text-[10px] px-1.5 h-5 border border-neutral-700 rounded-sm inline-flex items-center text-muted-foreground">
                    +{hiddenLabelsCount}
                  </div>
                )}
              </div>
              <ReleaseIconSelect
                task={task}
                onTaskChange={handleTaskChange}
              />
              <PrioritySelect
                task={task}
                onTaskChange={handleTaskChange}
                iconOnly
              />
              <AssigneeSelect
                task={task}
                organizationSlug={organizationSlug}
                onTaskChange={handleTaskChange}
              />
            </div>
          </div>
        </div>
      </div>
    </TaskContextMenu>
  );
}